import React from 'react'
import { useSequencerStore } from '@/stores/sequencerStore'
import { useMidiMappable } from '@/hooks/useMidiMappable'

/**
 * Three-state record toggle in Transport. Cycles:
 *
 *   off      — grey outline, no capture
 *   record   — red fill + pulse, note-ons overwrite cells
 *   overdub  — amber fill + slow pulse, note-ons merge on top of
 *              existing rich cells (velocity + microOffset update;
 *              fx / envelope / length / roll preserved)
 *
 * A single click moves forward one state. Clicking through all three
 * lets users cycle without a separate "clear" affordance.
 *
 * Mapped so a hardware controller's record button can drive it —
 * the MIDI binding is `transport.record` (kind: trigger).
 */
export const RecordButton: React.FC = () => {
  const recordMode = useSequencerStore(s => s.recordMode)
  const cycleRecordMode = useSequencerStore(s => s.cycleRecordMode)

  const map = useMidiMappable<HTMLButtonElement>({
    id: 'transport.record',
    kind: 'trigger',
    label: 'Record / Overdub',
    trigger: cycleRecordMode,
  })

  const cls =
    recordMode === 'record'
      ? 'bg-destructive text-white border-destructive animate-pulse shadow-[0_0_16px_rgba(220,38,38,0.6)]'
      : recordMode === 'overdub'
        ? 'bg-second text-[color:var(--second-ink,white)] border-second animate-pulse'
        : 'border-brd-subtle text-content-tertiary hover:text-destructive hover:border-destructive'

  const label =
    recordMode === 'record' ? 'REC' : recordMode === 'overdub' ? 'DUB' : 'REC'

  const title =
    recordMode === 'off'
      ? 'Arm record — next click enables note recording; click again to switch to overdub'
      : recordMode === 'record'
        ? 'Recording — click to switch to overdub (preserves existing cells)'
        : 'Overdubbing — click to disarm'

  return (
    <button
      type="button"
      ref={map.ref}
      onClick={e => {
        if (map.armForLearn(e)) return
        cycleRecordMode()
      }}
      aria-pressed={recordMode !== 'off'}
      title={title}
      className={`w-10 h-10 rounded-full grid place-items-center text-[11px] font-mono font-bold border-2 transition-colors ${cls}`}
    >
      {label}
    </button>
  )
}
